import type { SupabaseClient } from '@supabase/supabase-js';
import { InfrastructureError } from '@dma/shared';
import type { MediaRepository } from '../domain/repositories/MediaRepository.interface';

const BUCKET = 'site-media';

/**
 * Infrastructure implementation backed by Supabase Storage (see
 * infra/supabase/migrations/006_create_site_media_bucket.sql and
 * infra/supabase/policies/007_site_media_storage_policies.sql). Every file
 * gets a random name — the original filename is never trusted as a storage
 * path (avoids collisions and path-traversal-style characters).
 */
export class SupabaseMediaRepository implements MediaRepository {
  constructor(private readonly client: SupabaseClient) {}

  public async upload(file: File, folder: string): Promise<string> {
    const extension = file.name.includes('.') ? file.name.split('.').pop() : 'jpg';
    const path = `${folder}/${crypto.randomUUID()}.${extension}`;

    const { error } = await this.client.storage.from(BUCKET).upload(path, file, {
      contentType: file.type,
      upsert: false
    });

    if (error) {
      throw new InfrastructureError('IMAGE_UPLOAD_FAILED', error.message, { cause: error });
    }

    const { data } = this.client.storage.from(BUCKET).getPublicUrl(path);
    return data.publicUrl;
  }
}
